'use client';
import React, { useState, useRef } from 'react';
import { X, Upload, Loader2, CheckCircle2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { notificationStore } from '@/lib/notificationStore';
import { spendCredits, incrementPostCount, getNextPostCost } from '@/lib/appStore';
import { useAppStore } from '@/hooks/useAppStore';

interface PostProductModalProps {
  onClose: () => void;
  defaultCategory?: string;
}

const categories = [
  'Cars', 'Motorbikes', 'Trucks', 'Machinery', 'Electronics', 'Phones',
  'Fashion', 'Furniture', 'Property', 'Land', 'Agriculture', 'Livestock',
  'Auto Parts', 'Industrial Machinery', 'Services', 'Other Products'
];

const conditions = ['Brand New', 'Like New', 'Good', 'Fair', 'For Parts'];
const locations = ['Nairobi CBD', 'Westlands', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Machakos'];

type PostStep = 'details' | 'media' | 'pricing' | 'review' | 'success';

export default function PostProductModal({ onClose, defaultCategory }: PostProductModalProps) {
  const [step, setStep] = useState<PostStep>('details');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: defaultCategory || '',
    condition: '',
    location: '',
    price: '',
    contact: '',
    delivery: false,
    externalLink: '',
  });
  const [images, setImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const { credits } = useAppStore();
  const postCost = getNextPostCost();

  const updateForm = (key: string, value: string | boolean) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSlotClick = (i: number) => {
    if (images[i]) {
      // Remove image
      const newImages = [...images];
      newImages[i] = '';
      setImages(newImages);
      return;
    }
    setActiveSlot(i);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && activeSlot !== null) {
      const url = URL.createObjectURL(file);
      const newImages = [...images];
      newImages[activeSlot] = url;
      setImages(newImages);
      toast.success(`Photo ${activeSlot + 1} uploaded`);
      setActiveSlot(null);
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = () => {
    // Check if user has enough credits
    if (postCost > 0 && credits < postCost) {
      toast.error(`You need ${postCost} credits to post. Buy credits first.`);
      onClose();
      window.location.href = '/buy-credits-screen';
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      // Deduct credits and increment post count
      if (postCost > 0) spendCredits(postCost);
      incrementPostCount();
      setStep('success');
      toast.success('Listing posted successfully!');
      notificationStore.notifyNewProduct('You', form.title || 'New Listing', form.category || 'Classifieds');
    }, 1800);
  };

  const steps: PostStep[] = ['details', 'media', 'pricing', 'review', 'success'];
  const stepIdx = steps.indexOf(step);

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-t-3xl lg:rounded-3xl animate-slide-up max-h-[92vh] overflow-y-auto"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4"
          style={{ background: 'var(--card)', borderBottom: '1px solid var(--border)' }}>
          <div>
            <h3 className="text-base font-700 text-foreground">Post a Listing</h3>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Step {Math.min(stepIdx + 1, 4)} of 4 — {step === 'details' ? 'Details' : step === 'media' ? 'Photos' : step === 'pricing' ? 'Pricing' : 'Review'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl" style={{ border: '1px solid var(--border)' }}>
            <X size={16} style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>

        {/* Step progress */}
        {step !== 'success' && (
          <div className="flex gap-1 px-6 pt-4">
            {['details', 'media', 'pricing', 'review'].map((s, i) => (
              <div key={s} className="flex-1 h-1 rounded-full transition-all duration-300"
                style={{ background: i <= stepIdx ? 'var(--primary)' : 'var(--border)' }} />
            ))}
          </div>
        )}

        <div className="px-6 py-5">
          {/* Step 1: Details */}
          {step === 'details' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600 text-foreground">Title *</label>
                <input type="text" value={form.title} onChange={e => updateForm('title', e.target.value)}
                  placeholder="e.g. iPhone 14 Pro Max 256GB"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600 text-foreground">Description *</label>
                <textarea value={form.description} onChange={e => updateForm('description', e.target.value)}
                  placeholder="Describe your item in detail..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-600 text-foreground">Category *</label>
                  <select value={form.category} onChange={e => updateForm('category', e.target.value)}
                    className="w-full px-3 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: form.category ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                    <option value="">Select...</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-600 text-foreground">Condition *</label>
                  <select value={form.condition} onChange={e => updateForm('condition', e.target.value)}
                    className="w-full px-3 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: form.condition ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                    <option value="">Select...</option>
                    {conditions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600 text-foreground">Location *</label>
                <select value={form.location} onChange={e => updateForm('location', e.target.value)}
                  className="w-full px-3 py-3 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)', color: form.location ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                  <option value="">Select location...</option>
                  {locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600 text-foreground">Contact Number</label>
                <input type="tel" value={form.contact} onChange={e => updateForm('contact', e.target.value)}
                  placeholder="+254 7XX XXX XXX"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>

              <button onClick={() => {
                if (!form.title || !form.category || !form.condition || !form.location) {
                  toast.error('Please fill in all required fields');
                  return;
                }
                setStep('media');
              }}
                className="w-full py-3.5 rounded-2xl text-sm font-700 transition-all duration-150 active:scale-95 glow-green"
                style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Next: Add Photos
              </button>
            </div>
          )}

          {/* Step 2: Media */}
          {step === 'media' && (
            <div className="flex flex-col gap-4">
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Add up to 5 photos or videos. First photo will be the cover image.
              </p>

              <div className="grid grid-cols-3 gap-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <button key={`img-slot-${i}`}
                    onClick={() => handleSlotClick(i)}
                    className="aspect-square rounded-xl flex flex-col items-center justify-center transition-all duration-150 active:scale-95 overflow-hidden relative"
                    style={{ background: images[i] ? 'transparent' : 'var(--secondary)', border: `2px dashed ${images[i] ? 'var(--primary)' : 'var(--border)'}` }}>
                    {images[i] ? (
                      <>
                        <img src={images[i]} alt={`Product photo ${i + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                          style={{ background: 'rgba(0,0,0,0.5)' }}>
                          <X size={20} style={{ color: 'white' }} />
                        </div>
                      </>
                    ) : (
                      <>
                        <Camera size={20} style={{ color: 'var(--muted-foreground)' }} />
                        <span className="text-[10px] mt-1" style={{ color: 'var(--muted-foreground)' }}>
                          {i === 0 ? 'Cover' : `Photo ${i + 1}`}
                        </span>
                      </>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.2)' }}>
                <Upload size={16} style={{ color: 'var(--primary)' }} />
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  Tap a slot to upload from your device. Tap an uploaded photo to remove it.
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('details')}
                  className="flex-1 py-3 rounded-2xl text-sm font-600"
                  style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
                  Back
                </button>
                <button onClick={() => {
                  if (images.filter(Boolean).length === 0) {
                    toast.error('Please add at least one photo');
                    return;
                  }
                  setStep('pricing');
                }}
                  className="flex-1 py-3 rounded-2xl text-sm font-700 glow-green"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  Next: Pricing
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Pricing */}
          {step === 'pricing' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600 text-foreground">Price (KES) *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-700" style={{ color: 'var(--primary)' }}>KES</span>
                  <input type="number" value={form.price} onChange={e => updateForm('price', e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-14 pr-4 py-3 rounded-xl text-sm font-700 outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                <div>
                  <p className="text-sm font-600 text-foreground">Delivery Available</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Can you deliver this item?</p>
                </div>
                <button onClick={() => updateForm('delivery', !form.delivery)}
                  className="w-12 h-6 rounded-full transition-all duration-200 relative"
                  style={{ background: form.delivery ? 'var(--primary)' : 'var(--border)' }}>
                  <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200"
                    style={{ left: form.delivery ? '26px' : '2px' }} />
                </button>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600 text-foreground">External Link (optional)</label>
                <input type="url" value={form.externalLink} onChange={e => updateForm('externalLink', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('media')}
                  className="flex-1 py-3 rounded-2xl text-sm font-600"
                  style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
                  Back
                </button>
                <button onClick={() => {
                  if (!form.price) {
                    toast.error('Please enter a price');
                    return;
                  }
                  setStep('review');
                }}
                  className="flex-1 py-3 rounded-2xl text-sm font-700 glow-green"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  Review Listing
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 'review' && (
            <div className="flex flex-col gap-4">
              {/* Posting cost notice */}
              <div className="p-3 rounded-xl flex items-center gap-3"
                style={{ background: postCost === 0 ? 'rgba(0,200,83,0.08)' : 'rgba(255,179,0,0.08)', border: `1px solid ${postCost === 0 ? 'rgba(0,200,83,0.2)' : 'rgba(255,179,0,0.2)'}` }}>
                <span className="text-base">{postCost === 0 ? '🎉' : '⚡'}</span>
                <div>
                  <p className="text-xs font-700" style={{ color: postCost === 0 ? 'var(--primary)' : '#FFB300' }}>
                    {postCost === 0 ? 'First post is FREE!' : `This post costs ${postCost} credits`}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                    {postCost === 0 ? 'No credits deducted for your first listing.' : `You have ${credits} credits. ${credits >= postCost ? 'Sufficient balance.' : 'Insufficient — buy credits first.'}`}
                  </p>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                {images[0] && (
                  <img src={images[0]} alt="Cover photo" className="w-full h-40 object-cover" />
                )}
                <div className="p-4">
                  <h4 className="text-base font-700 text-foreground">{form.title}</h4>
                  <p className="text-xl font-800 mt-1" style={{ color: 'var(--primary)' }}>KES {Number(form.price).toLocaleString()}</p>
                  <p className="text-sm mt-2" style={{ color: 'var(--muted-foreground)' }}>{form.description}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[form.category, form.condition, form.location].filter(Boolean).map((tag) => (
                      <span key={tag} className="px-2 py-1 rounded-lg text-xs font-600"
                        style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)' }}>
                        {tag}
                      </span>
                    ))}
                    {form.delivery && (
                      <span className="px-2 py-1 rounded-lg text-xs font-600"
                        style={{ background: 'rgba(0,200,83,0.1)', color: 'var(--primary)' }}>
                        Delivery Available
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('pricing')}
                  className="flex-1 py-3 rounded-2xl text-sm font-600"
                  style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
                  Back
                </button>
                <button onClick={handleSubmit} disabled={loading}
                  className="flex-1 py-3 rounded-2xl text-sm font-700 glow-green flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  {loading ? <Loader2 size={16} className="animate-spin" /> : 'Publish Listing'}
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-5 text-center py-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center glow-green"
                style={{ background: 'rgba(0,200,83,0.12)', border: '2px solid var(--primary)' }}>
                <CheckCircle2 size={32} style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <p className="text-xl font-800 text-foreground mb-1">Listing Published!</p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Your listing is now live and visible to buyers.
                </p>
              </div>
              {/* 30-day expiry notice */}
              <div className="w-full rounded-2xl p-4 text-left"
                style={{ background: 'rgba(255,179,0,0.08)', border: '1px solid rgba(255,179,0,0.3)' }}>
                <div className="flex items-start gap-3">
                  <span className="text-lg">⏳</span>
                  <div>
                    <p className="text-sm font-700 text-foreground mb-0.5">Active for 30 Days</p>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      Your post will stay live for <span className="font-700 text-foreground">30 days</span>, then automatically move to your <span className="font-700 text-foreground">Reserve</span>. You can repost from your Reserve for <span className="font-700" style={{ color: 'var(--primary)' }}>KES 10</span> per repost.
                    </p>
                  </div>
                </div>
              </div>
              <button onClick={onClose}
                className="w-full py-3 rounded-2xl text-sm font-700 glow-green"
                style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
